/* Feb-2026 · Floating Exec + Ledger side-dock (admin-only).
 *
 * Fixed-position "small square" pop-up on the right side of every admin
 * page. Renders:
 *   • Exec Summary (YTD revenue / net income / cash / subs) — reuses
 *     the compact variant of `ExecSummary.jsx`
 *   • Ledger snapshot — trial-balance total DR/CR and balanced status
 *   • Recent-JE strip (last 3 postings) so the admin can see money
 *     moving without leaving whatever panel they're on
 *
 * The dock is collapsible: it starts open and remembers the user's
 * preference in localStorage. Never rendered outside /admin.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link, useLocation } from 'react-router-dom';
import { BookText, ChevronRight, ChevronLeft, RefreshCw, Loader2 } from 'lucide-react';
import ExecSummary from '@/pages/admin/ExecSummary';
import { adminAuth, BACKEND_URL } from '@/lib/api';

const API = `${BACKEND_URL}/api`;
const http = axios.create({ baseURL: API, withCredentials: true });
const KEY = 'admin_execdock_open_v1';

const fmtUSD = (v) => v == null ? '—' : `$${Math.abs(Number(v) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function AdminExecDock() {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith('/admin');
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem(KEY) !== '0'; } catch { return true; }
  });
  const [ledger, setLedger] = useState(null);
  const [busy, setBusy] = useState(false);
  const [authorized, setAuthorized] = useState(false);

  const reload = async () => {
    if (!isAdmin || !authorized) return;
    setBusy(true);
    try {
      const r = await http.get('/admin/gl/trial-balance');
      setLedger(r.data);
    } catch { /* silent — not every admin holds finance scope */ }
    finally { setBusy(false); }
  };

  useEffect(() => {
    if (!isAdmin || !authorized) return undefined;
    reload();
    const t = setInterval(reload, 60_000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, authorized]);

  useEffect(() => {
    const refresh = () => reload();
    window.addEventListener('finance:updated', refresh);
    return () => window.removeEventListener('finance:updated', refresh);
  });

  useEffect(() => {
    if (!isAdmin) {
      setAuthorized(false);
      return undefined;
    }
    let cancelled = false;
    const verify = async () => {
      if (!adminAuth.user) {
        if (!cancelled) setAuthorized(false);
        return;
      }
      try {
        await http.get('/admin/verify');
        if (!cancelled) setAuthorized(true);
      } catch {
        if (!cancelled) setAuthorized(false);
      }
    };
    verify();
    window.addEventListener('admin:authenticated', verify);
    window.addEventListener('admin:signed-out', verify);
    return () => {
      cancelled = true;
      window.removeEventListener('admin:authenticated', verify);
      window.removeEventListener('admin:signed-out', verify);
    };
  }, [isAdmin]);

  const toggle = () => {
    setOpen((v) => {
      const next = !v;
      try { localStorage.setItem(KEY, next ? '1' : '0'); } catch {}
      return next;
    });
  };

  if (!isAdmin || !authorized) return null;

  // Trial balance endpoint returns `rows: [{debits, credits, balance}]`.
  // Sum client-side so the dock stays useful even if the response shape
  // evolves (e.g. adds a `totals` block later).
  const rows = ledger?.rows || [];
  const drTotal = rows.reduce((a, r) => a + (Number(r.debits) || 0), 0);
  const crTotal = rows.reduce((a, r) => a + (Number(r.credits) || 0), 0);
  const balanced = ledger ? Math.abs(drTotal - crTotal) < 0.01 : null;
  const lines = rows.reduce((a, r) => a + (Number(r.entry_count) || 0), 0);

  return (
    <div
      className="fixed right-2 top-24 z-40 flex flex-col items-end"
      data-testid="admin-exec-dock"
    >
      {!open && (
        <button
          onClick={toggle}
          data-testid="admin-exec-dock-open"
          className="rounded-l-md border border-r-0 border-border bg-card px-2 py-3 shadow-md hover:bg-panel-header/40"
          title="Show Executive Summary"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
      )}
      {open && (
        <div className="w-72 rounded-md border border-border bg-card shadow-lg">
          <div className="flex items-center justify-between border-b border-border bg-panel-header/40 px-2 py-1.5">
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              Executive Dock
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={reload}
                disabled={busy}
                data-testid="admin-exec-dock-refresh"
                className="rounded-sm p-0.5 text-muted-foreground hover:text-foreground disabled:opacity-60"
                title="Refresh ledger snapshot"
              >
                {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
              </button>
              <button
                onClick={toggle}
                data-testid="admin-exec-dock-close"
                className="rounded-sm p-0.5 text-muted-foreground hover:text-foreground"
                title="Hide dock"
              >
                <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
          <div className="p-2 space-y-2">
            <ExecSummary compact />
            <Link to="/admin/finance?tab=ledger" className={`block rounded-md border p-2 transition-colors hover:border-primary ${balanced ? 'border-emerald-200 bg-emerald-50' : ledger ? 'border-rose-300 bg-rose-50' : 'border-border bg-panel-header/20'}`}
                  data-testid="admin-exec-dock-ledger">
              <div className="flex items-center gap-1 text-[9.5px] uppercase tracking-wider text-muted-foreground">
                <BookText className="h-2.5 w-2.5" /> Ledger · Trial Balance
              </div>
              <div className="mt-0.5 grid grid-cols-2 gap-1 text-[11.5px]">
                <div>
                  <div className="text-[9px] uppercase text-muted-foreground">DR</div>
                  <div className="font-mono-stat font-semibold">{fmtUSD(drTotal)}</div>
                </div>
                <div>
                  <div className="text-[9px] uppercase text-muted-foreground">CR</div>
                  <div className="font-mono-stat font-semibold">{fmtUSD(crTotal)}</div>
                </div>
              </div>
              <div className={`mt-1 text-[10.5px] font-semibold ${balanced ? 'text-emerald-700' : ledger ? 'text-rose-700' : 'text-muted-foreground'}`}>
                {ledger ? (balanced ? 'Balanced' : 'Not balanced') : '—'}
                {lines != null && <span className="ml-1 font-normal text-muted-foreground">· {lines} lines</span>}
              </div>
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
